import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { CreateMemeImageType } from "@/types/meme";
import { VideoTemplate } from "@/config/videoTemplates";
import { SuggestedImage } from "@/types/api";
import { ImageCheckLevel } from "@/types/api";
import { FILE_TYPE_ENUM } from "@/app/components/UI/VideoPlayer";
export enum ANIMATION_TAB_ENUM {
  gallery = "gallery",
  upload = "upload"
}

export enum ANIMATION_TYPE_ENUM {
  remix = "remix",
  upload = "upload",
  existingTemplate = "existingTemplate"
}

export type VideoTemplateAndType = VideoTemplate & {
  type: ANIMATION_TYPE_ENUM;
  fileType: FILE_TYPE_ENUM;
};

export type ImageError = {
  level: ImageCheckLevel;
  message: string;
};

interface MemeState {
  formData: {
    image: {
      uploaded?: File;
      croppedImage?: File;
      predefinedCharacter?: string;
      finalBlob?: Blob;
      imageId?: string;
      createMemeImageType?: CreateMemeImageType;
      currentSelectImageUrl?: string;
    };

    caption: {
      text: string;
    };

    animation: {
      hasAudio: boolean;
      existingMemeIdAsTemplate?: string;
      existingMemeUrlAsTemplate?: string;
      remixVideoTemplate?: string;
      remixVideoTemplateUrl?: string;
      temporaryMemeId?: string;
      uploadedAnimation?: string;
      uploadedAnimationType?: FILE_TYPE_ENUM;
      selectedAnimations: VideoTemplateAndType[];
      currentSelectAnimationItem?: VideoTemplateAndType;
      currentSelectAnimationTab: ANIMATION_TAB_ENUM;
      specialEffectType?: string;
    };
    proAnimation: {
      isProAnimation: boolean;
    };
  };

  errors: {
    image?: string;
    caption?: string;
    imageLevel?: ImageCheckLevel;
  };

  memeData: {
    memeId?: string;
    memeUrl?: string;
    visibility?: string;
    hasAudio?: boolean;
  };

  suggestedImages: SuggestedImage[];

  setField: <K extends keyof MemeState>(key: K, value: MemeState[K]) => void;
  setFormField: <T extends keyof MemeState["formData"], K extends keyof MemeState["formData"][T]>(
    category: T,
    key: K,
    value: MemeState["formData"][T][K]
  ) => void;
  setMemeDataField: <K extends keyof MemeState["memeData"]>(key: K, value: MemeState["memeData"][K]) => void;
  setError: <K extends keyof MemeState["errors"]>(key: K, value: MemeState["errors"][K]) => void;
  resetFormField: () => void;
  resetState: () => void;
  resetAnimationFields: () => void;
  resetImageFields: () => void;
  hasRequiredData: () => boolean;
  hasRequiredDataFree: () => boolean;
  addSelectedAnimation: (animation: VideoTemplateAndType) => void;
  removeSelectedAnimation: (animationId?: string) => void;
  setCurrentSelectAnimationItem: (animation: VideoTemplateAndType | undefined) => void;
  resetUploadAnimationFields: () => void;
  setSuggestedImages: (images: SuggestedImage[]) => void;
  setImageError: (error: ImageError) => void;
}

export const useMemeStore = create<MemeState>()(
  devtools(
    (set, get) => ({
      formData: {
        image: {
          uploaded: undefined,
          predefinedCharacter: undefined,
          finalBlob: undefined,
          imageId: undefined,
          createMemeImageType: undefined,
          currentSelectImageUrl: undefined
        },
        caption: {
          text: ""
        },
        animation: {
          hasAudio: false,
          selectedAnimations: [],
          currentSelectAnimationItem: undefined,
          currentSelectAnimationTab: ANIMATION_TAB_ENUM.gallery,
          specialEffectType: undefined
        },
        proAnimation: {
          isProAnimation: false
        }
      },
      errors: {},
      memeData: {},
      suggestedImages: [],
      setField: (key, value) =>
        set((state) => ({
          ...state,
          [key]: value
        })),

      setFormField: <T extends keyof MemeState["formData"], K extends keyof MemeState["formData"][T]>(
        category: T,
        key: K,
        value: MemeState["formData"][T][K]
      ) =>
        set((prev) => ({
          formData: {
            ...prev.formData,
            [category]: {
              ...(prev.formData[category] as Record<string, unknown>),
              [key]: value
            }
          }
        })),

      setMemeDataField: (key, value) =>
        set((state) => ({
          memeData: {
            ...state.memeData,
            [key]: value
          }
        })),

      setError: (key, value) =>
        set((state) => ({
          errors: {
            ...state.errors,
            [key]: value
          }
        })),

      resetFormField: () =>
        set({
          formData: {
            image: {
              uploaded: undefined,
              croppedImage: undefined,
              predefinedCharacter: undefined,
              finalBlob: undefined,
              imageId: undefined,
              createMemeImageType: undefined,
              currentSelectImageUrl: undefined
            },
            caption: {
              text: ""
            },
            animation: {
              hasAudio: false,
              selectedAnimations: [],
              currentSelectAnimationItem: undefined,
              currentSelectAnimationTab: ANIMATION_TAB_ENUM.gallery,
              specialEffectType: undefined
            },
            proAnimation: {
              isProAnimation: false
            }
          }
        }),

      resetState: () =>
        set({
          formData: {
            image: {
              uploaded: undefined,
              croppedImage: undefined,
              predefinedCharacter: undefined,
              finalBlob: undefined,
              imageId: undefined,
              createMemeImageType: undefined,
              currentSelectImageUrl: undefined
            },
            caption: {
              text: ""
            },
            animation: {
              hasAudio: false,
              selectedAnimations: [],
              currentSelectAnimationItem: undefined,
              currentSelectAnimationTab: ANIMATION_TAB_ENUM.gallery,
              specialEffectType: undefined
            },
            proAnimation: {
              isProAnimation: false
            }
          },
          errors: {},
          memeData: {},
          suggestedImages: []
        }),
      resetAnimationFields: () => {
        return set((state) => ({
          formData: {
            ...state.formData,
            animation: {
              hasAudio: false,
              existingMemeIdAsTemplate: undefined,
              existingMemeUrlAsTemplate: undefined,
              remixVideoTemplate: undefined,
              remixVideoTemplateUrl: undefined,
              temporaryMemeId: undefined,
              uploadedAnimation: undefined,
              uploadedAnimationType: undefined,
              selectedAnimations: state.formData.animation.selectedAnimations,
              currentSelectAnimationItem: undefined,
              currentSelectAnimationTab: ANIMATION_TAB_ENUM.gallery,
              specialEffectType: undefined
            }
          }
        }));
      },
      resetImageFields: () => {
        return set((state) => ({
          formData: {
            ...state.formData,
            image: {
              uploaded: undefined,
              croppedImage: undefined,
              predefinedCharacter: undefined,
              finalBlob: undefined,
              imageId: undefined,
              createMemeImageType: undefined,
              currentSelectImageUrl: undefined
            }
          },
          errors: {
            ...state.errors,
            image: undefined
          }
        }));
      },
      hasRequiredData: () => {
        const state = get();
        return !!(
          (state.formData.image.finalBlob ||
            state.formData.image.predefinedCharacter ||
            state.formData.image.imageId) &&
          state.formData.animation.currentSelectAnimationItem
        );
      },
      hasRequiredDataFree: () => {
        const state = get();
        return !!(state.formData.image.predefinedCharacter && state.formData.caption.text);
      },
      addSelectedAnimation: (animation: VideoTemplateAndType) => {
        set((state) => {
          const exists = state.formData.animation.selectedAnimations.some((a) => a.id === animation.id);

          if (!exists) {
            const newAnimations = [animation, ...state.formData.animation.selectedAnimations];

            return {
              formData: {
                ...state.formData,
                animation: {
                  ...state.formData.animation,
                  selectedAnimations: newAnimations
                }
              }
            };
          }
          return state;
        });
      },
      removeSelectedAnimation: (animationId?: string) => {
        set((state) => ({
          formData: {
            ...state.formData,
            animation: {
              ...state.formData.animation,
              selectedAnimations: animationId
                ? state.formData.animation.selectedAnimations.filter((a) => a.id !== animationId)
                : []
            }
          }
        }));
      },
      setCurrentSelectAnimationItem: (animation?: VideoTemplateAndType) => {
        if (!animation) {
          set((state) => ({
            formData: {
              ...state.formData,
              animation: {
                ...state.formData.animation,
                currentSelectAnimationItem: undefined
              }
            }
          }));
        } else {
          set((state) => ({
            formData: {
              ...state.formData,
              animation: {
                ...state.formData.animation,
                currentSelectAnimationItem: animation
              }
            }
          }));
        }
      },
      resetUploadAnimationFields: () => {
        set((state) => ({
          formData: {
            ...state.formData,
            animation: {
              ...state.formData.animation,
              uploadedAnimation: undefined,
              temporaryMemeId: undefined,
              uploadedAnimationType: undefined
            }
          }
        }));
      },
      setSuggestedImages: (images: SuggestedImage[]) => {
        set({ suggestedImages: images });
      },
      setImageError: (error: ImageError) => {
        set({
          errors: {
            ...get().errors,
            imageLevel: error.level,
            image: error.message
          }
        });
      }
    }),

    {
      name: "memeStore",
      enabled: process.env.NODE_ENV === "development"
    }
  )
);
